import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Lock, Mail, User, Phone, Building, ArrowRight } from "lucide-react";

export const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "tenant", // "tenant" | "landlord"
    phone: "",
    company_name: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const user = await register(formData);
      toast.success(`Account created! Welcome, ${user.name}`);
      if (user.role === "landlord") {
        navigate("/landlord/dashboard");
      } else {
        navigate("/tenant/dashboard");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FBFF] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center space-y-2">
        <Link to="/" className="inline-flex items-center space-x-2 text-2xl font-bold font-heading uppercase text-[#091F5C]">
          <span>FIND</span>
          <span className="text-[#334DAF]">|</span>
          <span>LET</span>
        </Link>
        <h2 className="text-2xl font-extrabold text-[#091F5C] font-heading">
          Create Your FIND LET Account
        </h2>
        <p className="text-xs text-[#4A5B78]">
          Join London's demand-driven rental ecosystem as a Renter or Landlord.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md px-4">
        <div className="bg-white py-8 px-6 sm:px-10 rounded-2xl border border-[#E5EBF5] shadow-xl space-y-6">
          
          {/* Role Switcher */}
          <div>
            <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2 text-center">
              Select Your Role
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, role: "tenant" })}
                data-testid="register-role-tenant"
                className={`py-2.5 rounded-xl text-xs font-bold transition-all ${
                  formData.role === "tenant"
                    ? "bg-[#091F5C] text-white shadow"
                    : "bg-[#E8F2FE] text-[#4A5B78] hover:bg-[#D0E4FE]"
                }`}
              >
                Tenant (Renters)
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, role: "landlord" })}
                data-testid="register-role-landlord"
                className={`py-2.5 rounded-xl text-xs font-bold transition-all ${
                  formData.role === "landlord"
                    ? "bg-[#091F5C] text-white shadow"
                    : "bg-[#E8F2FE] text-[#4A5B78] hover:bg-[#D0E4FE]"
                }`}
              >
                Landlord / Owner
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-1.5">
                Full Name *
              </label>
              <div className="relative">
                <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                <input
                  type="text"
                  required
                  placeholder="e.g. Eleanor Vane"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="register-name-input"
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-1.5">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                <input
                  type="email"
                  required
                  placeholder="name@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="register-email-input"
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-1.5">
                Password *
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                <input
                  type="password"
                  required
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="register-password-input"
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-1.5">
                  Phone Number
                </label>
                <input
                  type="tel"
                  placeholder="+44 7700 900000"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="register-phone-input"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#E5EBF5] text-sm font-mono"
                />
              </div>

              {formData.role === "landlord" && (
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-1.5">
                    Company (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Mayfair Estates Ltd"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    data-testid="register-company-input"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  />
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="register-submit-button"
              className="w-full py-3 rounded-xl bg-[#091F5C] hover:bg-[#091F5C] text-white font-bold text-sm shadow-md transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              <span>{submitting ? "Creating Account..." : "Create Account"}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="text-center text-xs text-[#4A5B78] pt-2 border-t">
            Already have an account?{" "}
            <Link to="/login" className="text-[#334DAF] font-bold hover:underline">
              Sign In
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
};
